import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent } from "react";
import { useTheme } from "next-themes";
import { imagineStore } from "@/stores/imagine.store";
import { useProjectFilesQuery } from "./use-project-files-query";
import { useCodePanelFiles } from "../hooks/use-codepanel-files";
import { useImageFolderDrop } from "../hooks/use-image-folder-drop";
import { useFileTreeSearch } from "../hooks/use-file-tree-search";
import { buildFileTree } from "../file-tree";
import {
  pickDefaultFile,
  inferLanguage,
  createMonacoOptions,
  defineImagineThemes,
} from "./utils";
import { isRasterImagePath, isSvgPath } from "../utils/image-preview";

type ControllerParams = {
  isActive: boolean;
  controlledSelectedFile?: string | null;
  onSelectedFileChange?: (path: string | null) => void;
};

export function useCodePanelController({
  isActive,
  controlledSelectedFile,
  onSelectedFileChange,
}: ControllerParams) {
  const { resolvedTheme } = useTheme();
  const projectId = imagineStore.use.projectId();
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const drafts = imagineStore.use.codeEditor.drafts();
  const setDraft = imagineStore.use.codeEditor.setDraft();
  const clearDraft = imagineStore.use.codeEditor.clearDraft();

  const { data, isLoading, error, isRefetching } = useProjectFilesQuery({
    projectId,
    isActive,
    sandboxInstrumented,
  });

  const { serverFiles, serverFilePaths, combinedFiles, fileMap } =
    useCodePanelFiles({
      apiFiles: data?.files,
      drafts,
    });

  const [uncontrolledSelectedFile, setUncontrolledSelectedFile] = useState<
    string | null
  >(null);
  const [expandedDirectories, setExpandedDirectories] = useState<Set<string>>(
    () => new Set(["src"]),
  );
  const [isSvgPreviewMode, setIsSvgPreviewMode] = useState(false);

  const selectedFile = controlledSelectedFile ?? uncontrolledSelectedFile;
  const setSelectedFile = useCallback(
    (path: string | null) => {
      if (onSelectedFileChange) onSelectedFileChange(path);
      else setUncontrolledSelectedFile(path);
    },
    [onSelectedFileChange],
  );

  const dragHandlers = useImageFolderDrop({
    serverFilePaths,
    setDraft,
    setSelectedFile,
  });

  const fileTree = useMemo(() => buildFileTree(combinedFiles), [combinedFiles]);
  const {
    query: fileSearchQuery,
    setQuery: setFileSearchQuery,
    isSearchActive,
    filteredTree: filteredFileTree,
    highlightMatches,
    handleSearchKeyDown,
  } = useFileTreeSearch({
    fileTree,
    onSelectFile: setSelectedFile,
  });

  useEffect(() => {
    if (!combinedFiles.length) {
      setSelectedFile(null);
      return;
    }

    if (selectedFile && fileMap.has(selectedFile)) {
      return;
    }

    const defaultFile = pickDefaultFile(serverFiles);
    if (defaultFile) {
      setSelectedFile(defaultFile);
      return;
    }

    const draftFallback = combinedFiles[0]?.path ?? null;
    setSelectedFile(draftFallback);
  }, [combinedFiles, serverFiles, fileMap, selectedFile, setSelectedFile]);

  const selectedFileMeta = selectedFile ? fileMap.get(selectedFile) : undefined;
  const selectedContent = selectedFileMeta?.content ?? "";
  const selectedDraft = selectedFile ? drafts[selectedFile] : undefined;
  const editorValue = selectedDraft?.content ?? selectedContent;
  const isSelectedFileReadOnly = Boolean(selectedFileMeta?.readOnly);
  const isSelectedFileDirty = selectedDraft !== undefined;
  const currentFileRef = useRef<string | null>(selectedFile);

  const isRasterImage = isRasterImagePath(selectedFile ?? null);
  const isSvg = isSvgPath(selectedFile ?? null);
  const fileKind: "text" | "svg" | "raster" = isRasterImage
    ? "raster"
    : isSvg
      ? "svg"
      : "text";

  useEffect(() => {
    setIsSvgPreviewMode(isSvg);
  }, [selectedFile, isSvg]);

  useEffect(() => {
    currentFileRef.current = selectedFile;
  }, [selectedFile]);

  const toggleDirectory = useCallback((path: string) => {
    setExpandedDirectories((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleEditorDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }, []);

  const handleEditorDrop = useCallback(
    (event: DragEvent) => {
      if (!selectedFile) {
        // No file selected, drop to root
        dragHandlers.handleRootDrop(event as DragEvent<HTMLDivElement>);
        return;
      }

      // Get the directory of the currently selected file
      const pathParts = selectedFile.split("/");
      const directory =
        pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : "";

      dragHandlers.handleDirectoryDrop(
        event as DragEvent<HTMLDivElement>,
        directory,
      );
    },
    [selectedFile, dragHandlers],
  );

  const handleEditorChange = useCallback(
    (value?: string) => {
      if (selectedFile !== currentFileRef.current) {
        return;
      }

      if (!selectedFile || isSelectedFileReadOnly) {
        return;
      }

      const nextValue = value ?? "";
      if (nextValue === selectedContent) {
        clearDraft(selectedFile);
        return;
      }
      setDraft(selectedFile, nextValue);
    },
    [
      selectedFile,
      selectedContent,
      isSelectedFileReadOnly,
      setDraft,
      clearDraft,
    ],
  );

  const editorLanguage = inferLanguage(selectedFile);
  const editorTheme =
    resolvedTheme === "dark" ? "imagine-dark" : "imagine-light";
  const monacoOptions = useMemo(
    () =>
      createMonacoOptions({
        isReadOnly: isSelectedFileReadOnly,
        isSvg,
      }),
    [isSelectedFileReadOnly, isSvg],
  );

  const fileExplorerProps = {
    searchQuery: fileSearchQuery,
    onSearchQueryChange: setFileSearchQuery,
    onSearchKeyDown: handleSearchKeyDown,
    filteredTree: filteredFileTree,
    isSearchActive,
    highlightMatches,
    expandedDirectories,
    onToggleDirectory: toggleDirectory,
    selectedFile,
    onSelectFile: handleFileSelection,
    drafts,
    dragHandlers: {
      draggedDirectory: dragHandlers.draggedDirectory,
      onDirectoryDragOver: dragHandlers.handleDirectoryDragOver,
      onDirectoryDragLeave: dragHandlers.handleDirectoryDragLeave,
      onDirectoryDrop: dragHandlers.handleDirectoryDrop,
    },
    isLoading,
    isRefetching,
    hasError: Boolean(error),
    onRootDragOver: dragHandlers.handleRootDragOver,
    onRootDragLeave: dragHandlers.handleRootDragLeave,
    onRootDrop: dragHandlers.handleRootDrop,
    isRootDragTarget: dragHandlers.isRootDragTarget,
  };

  const editorSurfaceProps = {
    selectedFile,
    editorValue,
    isDirty: isSelectedFileDirty,
    isReadOnly: isSelectedFileReadOnly,
    fileKind,
    isSvgPreviewMode,
    onToggleSvgPreview: () => setIsSvgPreviewMode((prev) => !prev),
    onEditorChange: handleEditorChange,
    editorLanguage,
    editorTheme,
    monacoOptions,
    beforeMount: defineImagineThemes,
    onEditorDrop: handleEditorDrop,
    onEditorDragOver: handleEditorDragOver,
  };

  return {
    fileExplorerProps,
    editorSurfaceProps,
  };

  function handleFileSelection(path: string) {
    setSelectedFile(path);
  }
}
